<template>
  <div v-if="option" class="bg-white rounded-lg shadow ring-1 ring-black ring-opacity-5 p-4 mb-4 mt-4">
    <h3 class="text-sm font-medium text-gray-700 mb-2">Dépenses mensuelles par fournisseur</h3>
    <v-chart :option="option" autoresize style="height: 420px; width: 100%"/>
  </div>
</template>

<script setup>
import {use} from 'echarts/core'
import {CanvasRenderer} from 'echarts/renderers'
import {BarChart} from 'echarts/charts'
import {TooltipComponent, LegendComponent, GridComponent} from 'echarts/components'

use([CanvasRenderer, BarChart, TooltipComponent, LegendComponent, GridComponent])
</script>

<script>
import VChart from 'vue-echarts'

// Un fournisseur a sa propre série dès qu'il pèse au moins ce pourcentage du total annuel ;
// seuls les plus petits sont regroupés dans « Autres »
const SUPPLIER_SHARE_THRESHOLD = 0.01

export default {
  components: {VChart},
  props: {
    transactions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    option() {
      // Dépenses uniquement (montants négatifs)
      const expenses = this.transactions.filter(t => parseFloat(t.amount) < 0 && t.date)
      if (!expenses.length) {
        return null
      }

      const months = [...new Set(expenses.map(t => t.date.substring(0, 7)))].sort()
      const totals = {}
      const perSupplierMonth = {}

      expenses.forEach(t => {
        const supplier = (t.company && t.company.name) || t.from_name || 'Inconnu'
        const month = t.date.substring(0, 7)
        const amount = Math.abs(parseFloat(t.amount))
        totals[supplier] = (totals[supplier] || 0) + amount
        perSupplierMonth[supplier] = perSupplierMonth[supplier] || {}
        perSupplierMonth[supplier][month] = (perSupplierMonth[supplier][month] || 0) + amount
      })

      // Les fournisseurs significatifs en séries dédiées, le reste regroupé en « Autres »
      const grandTotal = Object.values(totals).reduce((sum, v) => sum + v, 0)
      const topSuppliers = Object.entries(totals)
          .sort((a, b) => b[1] - a[1])
          .filter(([, total]) => total >= grandTotal * SUPPLIER_SHARE_THRESHOLD)
          .map(([supplier]) => supplier)

      const series = topSuppliers.map(supplier => ({
        name: supplier,
        type: 'bar',
        stack: 'total',
        emphasis: {focus: 'series'},
        data: months.map(month => +(perSupplierMonth[supplier][month] || 0).toFixed(2)),
      }))

      const others = months.map(month => {
        let sum = 0
        Object.keys(perSupplierMonth).forEach(supplier => {
          if (!topSuppliers.includes(supplier)) {
            sum += perSupplierMonth[supplier][month] || 0
          }
        })
        return +sum.toFixed(2)
      })
      const othersTotal = others.reduce((sum, v) => sum + v, 0)
      if (othersTotal > 0) {
        series.push({
          name: 'Autres',
          type: 'bar',
          stack: 'total',
          itemStyle: {color: '#d1d5db'},
          data: others,
        })
      }

      // Somme annuelle par fournisseur, affichée dans la légende
      const annualTotals = {...totals, Autres: othersTotal}
      const formatTotal = value => Math.round(value).toLocaleString('fr-FR') + ' €'

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {type: 'shadow'},
          // N'affiche que les fournisseurs réellement concernés sur le mois survolé
          formatter: params => {
            const actives = params.filter(p => p.value > 0)
            if (!actives.length) {
              return `<b>${params[0].axisValue}</b><br/>Aucune dépense`
            }
            const monthTotal = actives.reduce((sum, p) => sum + p.value, 0)
            const lines = actives.map(p =>
                `${p.marker} ${p.seriesName} <span style="float:right;margin-left:16px;font-weight:600">${p.value.toFixed(2)} €</span>`
            )
            return `<b>${params[0].axisValue}</b><br/>` + lines.join('<br/>')
                + `<br/><b>Total<span style="float:right;margin-left:16px">${monthTotal.toFixed(2)} €</span></b>`
          },
        },
        legend: {
          type: 'scroll',
          bottom: 0,
          formatter: name => `${name} — ${formatTotal(annualTotals[name] || 0)}`,
        },
        grid: {left: 60, right: 20, top: 20, bottom: 40},
        xAxis: {type: 'category', data: months},
        yAxis: {type: 'value', axisLabel: {formatter: '{value} €'}},
        series,
      }
    },
  },
}
</script>
